import axios from 'axios';
import imageCompression from 'browser-image-compression';
import { S3Client, PutObjectCommand } from '@aws-sdk/client-s3';
import { getUploadConfig, UPLOAD_SERVICE_TYPES } from './uploadConfig';

// Cloudflare R2 (S3-compatible) – dùng key từ env
const R2_ACCOUNT_ID = process.env.REACT_APP_R2_ACCOUNT_ID || '';
const R2_ENDPOINT = R2_ACCOUNT_ID ? `https://${R2_ACCOUNT_ID}.r2.cloudflarestorage.com` : '';
const R2_BUCKET_NAME = process.env.REACT_APP_R2_BUCKET_NAME || '';
const R2_PUBLIC_URL = process.env.REACT_APP_R2_PUBLIC_URL || '';

const r2Client = R2_ENDPOINT
  ? new S3Client({
      region: 'auto',
      endpoint: R2_ENDPOINT,
      credentials: {
        accessKeyId: process.env.REACT_APP_R2_ACCESS_KEY_ID || '',
        secretAccessKey: process.env.REACT_APP_R2_SECRET_ACCESS_KEY || '',
      },
      forcePathStyle: false,
    })
  : null;

const CLOUD_NAME = process.env.REACT_APP_CLOUDINARY_CLOUD_NAME;
const UPLOAD_PRESET = process.env.REACT_APP_CLOUDINARY_UPLOAD_PRESET;
const API_KEY = process.env.REACT_APP_CLOUDINARY_API_KEY;

const COMPRESSION_OPTIONS = {
  maxSizeMB: 0.4, // Giảm xuống vì WebP đã nhỏ hơn
  maxWidthOrHeight: 1200, // Giảm cho mobile
  useWebWorker: true,
};

const ensureConfig = () => {
  if (!CLOUD_NAME || !UPLOAD_PRESET) {
    throw new Error(
      'Thiếu cấu hình Cloudinary. Vui lòng set REACT_APP_CLOUDINARY_CLOUD_NAME và REACT_APP_CLOUDINARY_UPLOAD_PRESET.',
    );
  }
};

/**
 * Kiểm tra browser có support WebP không
 * @returns {boolean}
 */
function supportsWebP() {
  try {
    const canvas = document.createElement('canvas');
    canvas.width = 1;
    canvas.height = 1;
    return canvas.toDataURL('image/webp').indexOf('data:image/webp') === 0;
  } catch {
    return false;
  }
}

/**
 * Convert ảnh sang WebP format để giảm dung lượng
 * @param {File} file - File ảnh cần convert
 * @param {number} quality - Chất lượng WebP (0-1), mặc định 0.8
 * @returns {Promise<Blob>} Blob WebP
 */
function convertToWebP(file, quality = 0.8) {
  return new Promise((resolve, reject) => {
    // Nếu browser không support WebP, return file gốc dưới dạng Blob
    if (!supportsWebP()) {
      file
        .arrayBuffer()
        .then((buffer) => resolve(new Blob([buffer], { type: file.type })))
        .catch(reject);
      return;
    }

    const img = new Image();
    img.src = URL.createObjectURL(file);

    img.onload = () => {
      const canvas = document.createElement('canvas');
      canvas.width = img.width;
      canvas.height = img.height;

      const ctx = canvas.getContext('2d');
      if (!ctx) {
        URL.revokeObjectURL(img.src);
        reject(new Error('Không thể tạo canvas context'));
        return;
      }

      ctx.drawImage(img, 0, 0);

      canvas.toBlob(
        (blob) => {
          URL.revokeObjectURL(img.src);
          if (blob) {
            resolve(blob);
          } else {
            reject(new Error('Không thể convert sang WebP'));
          }
        },
        'image/webp',
        quality,
      );
    };

    img.onerror = () => {
      URL.revokeObjectURL(img.src);
      reject(new Error('Lỗi khi load ảnh'));
    };
  });
}

/**
 * Upload ảnh lên Cloudinary
 * @param {File} file - File ảnh đã nén
 * @param {object} options - Các tuỳ chọn upload
 * @returns {Promise<object>} Dữ liệu trả về từ Cloudinary
 */
const uploadImageToCloudinary = async (file, options = {}) => {
  ensureConfig();

  const formData = new FormData();
  formData.append('file', file);
  formData.append('upload_preset', UPLOAD_PRESET);
  if (API_KEY) {
    formData.append('api_key', API_KEY);
  }
  if (options.folder) {
    formData.append('folder', options.folder);
  }
  if (options.tags) {
    formData.append('tags', options.tags);
  }

  const isVideo = file.type?.startsWith('video/');
  const resourceType = isVideo ? 'video' : 'image';
  const uploadUrl = `https://api.cloudinary.com/v1_1/${CLOUD_NAME}/${resourceType}/upload`;

  const response = await axios.post(uploadUrl, formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
    onUploadProgress: options.onUploadProgress,
  });

  return response.data;
};

/**
 * Upload ảnh lên R2 (Cloudflare) dùng key từ env
 * Env: REACT_APP_R2_ACCOUNT_ID, REACT_APP_R2_ACCESS_KEY_ID, REACT_APP_R2_SECRET_ACCESS_KEY, REACT_APP_R2_BUCKET_NAME, REACT_APP_R2_PUBLIC_URL
 * @param {File} file - File ảnh đã nén
 * @param {object} options - Các tuỳ chọn upload (folder)
 * @returns {Promise<object>} { secure_url, url }
 */
const uploadImageToR2 = async (file, options = {}) => {
  if (!r2Client || !R2_BUCKET_NAME) {
    throw new Error(
      'R2 chưa cấu hình: cần REACT_APP_R2_ACCOUNT_ID, REACT_APP_R2_ACCESS_KEY_ID, REACT_APP_R2_SECRET_ACCESS_KEY, REACT_APP_R2_BUCKET_NAME',
    );
  }

  const ext = file.name?.match(/\.([a-zA-Z0-9]+)$/)?.[1]?.toLowerCase() || 'jpg';
  const prefix = options.folder ? `${options.folder.replace(/\/$/, '')}/` : 'images/';
  const key = `${prefix}${Date.now()}-${Math.random().toString(36).slice(2, 9)}.${ext}`;

  const contentTypeMap = {
    jpg: 'image/jpeg',
    jpeg: 'image/jpeg',
    png: 'image/png',
    gif: 'image/gif',
    webp: 'image/webp',
    mp4: 'video/mp4',
    webm: 'video/webm',
    mov: 'video/quicktime',
    avi: 'video/x-msvideo',
    mkv: 'video/x-matroska',
    ogv: 'video/ogg',
  };
  const isVideo = file.type.startsWith('video/');
  const contentType = contentTypeMap[ext] || (isVideo ? 'video/mp4' : 'image/jpeg');

  try {
    const arrayBuffer = await file.arrayBuffer();
    const body = new Uint8Array(arrayBuffer);

    await r2Client.send(
      new PutObjectCommand({
        Bucket: R2_BUCKET_NAME,
        Key: key,
        Body: body,
        ContentType: contentType,
      }),
    );

    const url = R2_PUBLIC_URL
      ? `${R2_PUBLIC_URL.replace(/\/$/, '')}/${key}`
      : `https://${R2_BUCKET_NAME}.r2.dev/${key}`;
    return { secure_url: url, url };
  } catch (error) {
    throw new Error(`R2 upload failed: ${error?.message || 'Unknown error'}`);
  }
};

export const cloudinaryService = {
  /**
   * Nén ảnh trước khi upload để giảm dung lượng.
   * @param {File} file - File ảnh gốc
   * @param {object} options - Các tuỳ chọn nén
   * @param {number} options.maxSizeMB - Kích thước tối đa (MB), mặc định 0.4
   * @param {number} options.maxWidthOrHeight - Chiều rộng/cao tối đa, mặc định 1200
   * @param {boolean} options.convertToWebP - Convert sang WebP sau khi nén, mặc định true
   * @param {number} options.webpQuality - Chất lượng WebP (0-1), mặc định 0.8
   * @returns {Promise<File>} File đã được nén (và convert sang WebP nếu enabled)
   */
  compressImage: async (file, options = {}) => {
    const compressionOptions = {
      maxSizeMB: options.maxSizeMB || COMPRESSION_OPTIONS.maxSizeMB,
      maxWidthOrHeight: options.maxWidthOrHeight || COMPRESSION_OPTIONS.maxWidthOrHeight,
      useWebWorker: COMPRESSION_OPTIONS.useWebWorker,
    };

    // Bước 1: Compress/resize trước
    const compressed = await imageCompression(file, compressionOptions);

    // Bước 2: Convert sang WebP nếu enabled (mặc định true)
    const shouldConvertToWebP = options.convertToWebP !== false;
    if (shouldConvertToWebP) {
      try {
        const webpQuality = options.webpQuality || 0.8;
        const webpBlob = await convertToWebP(compressed, webpQuality);
        const webpFile = new File([webpBlob], compressed.name.replace(/\.[^/.]+$/, '.webp'), { type: 'image/webp' });
        return webpFile;
      } catch (error) {
        // Fallback: nếu WebP fail, dùng compressed file gốc
        console.warn('WebP conversion failed, using compressed file:', error?.message);
        return compressed;
      }
    }

    return compressed;
  },

  /**
   * Upload ảnh lên Cloudinary sau khi nén và convert WebP
   * @param {File} file - File ảnh gốc
   * @param {object} options - Các tuỳ chọn upload
   * @param {object} options.compression - Options cho compression (maxSizeMB, maxWidthOrHeight, convertToWebP, webpQuality)
   * @param {string} options.folder - Folder trên Cloudinary/R2
   * @param {Function} options.onUploadProgress - Callback cho upload progress
   * @param {string} options.tags - Tags cho Cloudinary
   * @returns {Promise<object>} Dữ liệu trả về từ Cloudinary hoặc R2
   */
  uploadImage: async (file, options = {}) => {
    // Chỉ compress nếu là file ảnh
    const isImage = file.type.startsWith('image/');
    let processedFile = file;

    if (isImage) {
      // Bước 1: Compress/resize và convert sang WebP (mặc định enabled)
      processedFile = await cloudinaryService.compressImage(file, {
        convertToWebP: options.compression?.convertToWebP !== false, // Mặc định convert sang WebP
        webpQuality: options.compression?.webpQuality || 0.8,
        maxSizeMB: options.compression?.maxSizeMB,
        maxWidthOrHeight: options.compression?.maxWidthOrHeight,
      });
    }

    try {
      // Lấy config từ API (hoặc cache)
      const service = await getUploadConfig();

      if (service === UPLOAD_SERVICE_TYPES.R2) {
        // Thử upload lên R2
        try {
          return await uploadImageToR2(processedFile, options);
        } catch (r2Error) {
          // Nếu R2 fail, fallback sang Cloudinary
          console.warn(`R2 upload failed, falling back to Cloudinary: ${r2Error?.message || 'Unknown error'}`);
          try {
            return await uploadImageToCloudinary(processedFile, options);
          } catch (cloudinaryError) {
            throw new Error(
              `Upload failed: R2 error - ${r2Error?.message || 'Unknown'}, Cloudinary error - ${
                cloudinaryError?.message || 'Unknown'
              }`,
            );
          }
        }
      } else {
        // Mặc định dùng Cloudinary
        try {
          return await uploadImageToCloudinary(processedFile, options);
        } catch (cloudinaryError) {
          // Nếu Cloudinary fail, fallback sang R2
          console.warn(`Cloudinary upload failed, falling back to R2: ${cloudinaryError?.message || 'Unknown error'}`);
          try {
            return await uploadImageToR2(processedFile, options);
          } catch (r2Error) {
            throw new Error(
              `Upload failed: Cloudinary error - ${cloudinaryError?.message || 'Unknown'}, R2 error - ${
                r2Error?.message || 'Unknown'
              }`,
            );
          }
        }
      }
    } catch (error) {
      throw new Error(`Failed to upload image: ${error.message}`);
    }
  },
};
